import { useMemo } from 'react';
import { useArticlesList } from '@/hooks/cms/useArticlesList';
import { ArticleSummaryCard } from '@/components/cms/ArticleSummaryCard';
import { libraryNavIcons } from '@/components/Hub/hubNavIcons';
import { defineHubSection, type HubSectionData } from '@/components/Hub/hubSectionTypes';
import { shuffled } from '@/components/Hub/hubShuffle';
import { HUB_LIBRARY_PATH } from '@/components/Hub/hubRoutes';
import type { Tracking } from '@/lib/heap/trackingAttributes';

const MAX_ARTICLES = 6;

const ARTICLE_TRACKING: Tracking = { click: 'Hub Article Opened', location: 'hub-sheet', buttonName: 'hub-article' };

const useLibraryData = (): HubSectionData => {
    const { items, isLoading, error } = useArticlesList();

    // The Library is not career-filtered so "pick six" is a shuffle. `items` only changes identity
    // once per panel open, when the fetch resolves. The order therefore holds while it is on screen.
    const slides = useMemo(
        () => shuffled(items).slice(0, MAX_ARTICLES).map(item => ({
            id: item.slug,
            render: () => <ArticleSummaryCard item={item} variant="compact" tracking={ARTICLE_TRACKING} />,
        })),
        [items],
    );

    return { slides, isLoading, isError: Boolean(error) };
};

export const librarySection = defineHubSection({
    id: 'library',
    title: 'Library',
    blurb: 'Explore articles with career insight, advice, and inspiration.',
    navIcons: libraryNavIcons,
    viewAll: { to: HUB_LIBRARY_PATH, label: 'View all' },
    endCard: {
        to: HUB_LIBRARY_PATH,
        heading: 'Check out some more Insights',
        actionLabel: 'Go to Library',
        groundClassName: 'bg-gz-gold-100 dark:bg-gz-gold-200',
        zigzagClassName: 'bg-gz-gold-200 dark:bg-gz-gold-100',
        zigzagOpacityClassName: 'opacity-30 dark:opacity-50',
    },
    itemBasisClassName: 'basis-[86%] sm:basis-[82%]',
    useData: useLibraryData,
});
